import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckIcon, PencilIcon, PlayIcon, Trash2Icon } from 'lucide-react';
import { HeaderStage } from '../components/HeaderStage';
import { TrackTable } from '../components/TrackTable';
import { useLibrary } from '../contexts/LibraryContext';
import { usePlayer } from '../contexts/PlayerContext';
export function Playlist() {
  const {
    id = ''
  } = useParams();
  const navigate = useNavigate();
  const {
    playlists,
    renamePlaylist,
    deletePlaylist,
    removeFromPlaylist,
    updateNote
  } = useLibrary();
  const {
    play
  } = usePlayer();
  const playlist = playlists.find(p => p.id === id);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(playlist?.name ?? '');
  if (!playlist) {
    return <div className="px-5 pb-16 pt-8 sm:px-8 lg:px-12" data-id="element-373" data-mp="15hm0hwrp0s9v">
        <h1 className="font-display text-3xl text-cream" data-id="element-374" data-mp="10iizqoiqx87p">That set is gone</h1>
        <p className="mt-2 text-sm text-muted" data-id="element-375" data-mp="3k7jvq7tn3vhg">It may have been deleted from this browser.</p>
        <Link to="/library" className="mt-6 inline-block rounded-full border border-ink-line px-5 py-2.5 text-sm text-cream transition-colors duration-150 ease-night hover:border-gold/60 hover:text-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-376" data-mp="3oy9z5fii9o9i">
          Back to library
        </Link>
      </div>;
  }
  const covers = playlist.tracks.slice(0, 4);
  const commitName = () => {
    renamePlaylist(playlist.id, draft);
    setEditing(false);
  };
  return <div className="relative px-5 pb-16 pt-8 sm:px-8 lg:px-12" data-id="element-377" data-mp="3txmukong03np">
      <HeaderStage artwork={covers[0]?.artwork} data-id="element-378" data-mp="3tzh65cctqjf4" />
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.26,
      ease: [0.23, 1, 0.32, 1]
    }} className="relative z-10 flex flex-col gap-6 border-b border-ink-line pb-10 sm:flex-row sm:items-end" data-id="element-379" data-mp="2qadsaqh1s8b">
        <div className="grid h-40 w-40 flex-none grid-cols-2 grid-rows-2 overflow-hidden rounded-md bg-ink-raised shadow-2xl shadow-black/50 ring-1 ring-cream/10" data-id="element-380" data-mp="t0svwb0xcbnw">
          {covers.length === 0 && <span className="col-span-2 row-span-2" data-id="element-381" data-mp="jb9c935e0drz" />}
          {covers.map((track, i) => <img key={track.id} src={track.artworkSmall} alt="" className={`h-full w-full object-cover ${covers.length === 1 ? 'col-span-2 row-span-2' : ''}`} style={{
          gridColumn: covers.length === 1 ? 'span 2' : undefined
        }} loading={i > 1 ? 'lazy' : undefined} data-id="element-382" data-mp="f9nvh6pqffci" data-mpi={i} />)}
        </div>

        <div className="min-w-0 flex-1" data-id="element-383" data-mp="e88flnkkjpm">
          <p className="text-sm text-muted" data-id="element-384" data-mp="3sgzdcaq00tf2">Playlist · saved on this device</p>
          {editing ? <div className="mt-2 flex items-center gap-2" data-id="element-385" data-mp="1uqply1ilw5h2">
              <input autoFocus value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => e.key === 'Enter' && commitName()} aria-label="Playlist name" className="w-full max-w-md rounded border border-ink-line bg-ink-panel px-3 py-2 font-display text-2xl text-cream focus:border-gold/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-386" data-mp="1uo3fk57gbw0f" />
              <button type="button" onClick={commitName} aria-label="Save playlist name" className="rounded-full bg-gold p-2 text-ink-base focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-387" data-mp="3mfyr04takwjq">
                <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-388" data-mp="2ymys6gvn4m7z" />
              </button>
            </div> : <h1 className="mt-2 flex items-center gap-3 font-display text-4xl leading-tight text-cream" data-id="element-389" data-mp="dy2odh0xic38">
              <span className="truncate" data-id="element-390" data-mp="ix5hc33mwruu">{playlist.name}</span>
              <button type="button" onClick={() => {
            setDraft(playlist.name);
            setEditing(true);
          }} aria-label="Rename playlist" className="flex-none rounded-full p-1.5 text-muted transition-colors duration-150 ease-night hover:text-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-391" data-mp="ivaweosd4zqr">
                <PencilIcon className="h-4 w-4" aria-hidden="true" data-id="element-392" data-mp="10ekt0f1uzypa" />
              </button>
            </h1>}

          <input value={playlist.note} onChange={e => updateNote(playlist.id, e.target.value)} placeholder="Add a liner note…" aria-label="Playlist note" className="mt-3 w-full max-w-lg rounded border border-transparent bg-transparent px-0 py-1 text-sm italic text-muted placeholder:text-muted/70 focus:border-b-gold/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-393" data-mp="vgjapwc9c3ev" />

          <div className="mt-5 flex items-center gap-3" data-id="element-394" data-mp="1e4ei8yha0kzj">
            <button type="button" onClick={() => play(playlist.tracks, 0)} disabled={playlist.tracks.length === 0} className="flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-medium text-ink-base transition-transform duration-150 ease-night hover:scale-[1.02] disabled:opacity-40 disabled:hover:scale-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft focus-visible:ring-offset-2 focus-visible:ring-offset-ink-base" data-id="element-395" data-mp="1xtc9jtcnzu6g">
              <PlayIcon className="h-4 w-4" aria-hidden="true" data-id="element-396" data-mp="1jucnmtgjdtdz" />
              Play set
            </button>
            <span className="text-xs text-muted" data-id="element-397" data-mp="151iw29nzpmsy">
              {playlist.tracks.length} {playlist.tracks.length === 1 ? 'track' : 'tracks'}
            </span>
            <button type="button" onClick={() => {
            deletePlaylist(playlist.id);
            navigate('/library');
          }} className="ml-auto flex items-center gap-1.5 rounded-full border border-ink-line px-3.5 py-2 text-xs text-muted transition-colors duration-150 ease-night hover:border-iris-soft/60 hover:text-iris-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-398" data-mp="w4056ono0e95">
              <Trash2Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-399" data-mp="2p0c8mi9gkoen" />
              Delete
            </button>
          </div>
        </div>
      </motion.div>

      {playlist.tracks.length === 0 ? <p className="relative z-10 mt-10 max-w-md text-sm leading-relaxed text-muted" data-id="element-400" data-mp="3gqh0t33gmfqf">
          Empty for now. Use the <span className="text-cream" data-id="element-401" data-mp="3co2do6srcttc">＋</span> beside any track in Home or Search to drop it
          in here.
        </p> : <div className="relative z-10 mt-8 overflow-x-auto scroll-thin" data-id="element-402" data-mp="37s4za6121xj7">
          <TrackTable tracks={playlist.tracks} onRemove={track => removeFromPlaylist(playlist.id, track.id)} data-id="element-403" data-mp="21bjjp5r97gtj" />
        </div>}
    </div>;
}